import { useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  Checkbox,
  NumberStepper,
  SegmentedControl,
  Select,
  Spinner,
  Switch,
} from "@smart/itops-sb-design-system-dom";
import {
  TimeZone,
  buildTimeZoneList,
  findLocalTimezone,
} from "@smart/itops-utils-basic";
import { useAnalyticsEvent, useUser } from "@smart/manage-hooks-dom";

import { ReminderScheduleConfirmModal } from "./reminder-schedule-confirm-modal";
import { SharedFormsSelect } from "./shared-forms-select";
import { ReminderScheduleInput, ReminderScheduleValue } from "../../types";

type ReminderScheduleSettingsProps = {
  value?: ReminderScheduleValue | null;
  update: (value: ReminderScheduleInput) => Promise<void>;
  loading?: boolean;
  loadScheduledReminderCount?: () => Promise<number>;
};

const resolveTimezoneCode = (
  saved: string | null | undefined,
  timeZoneOptions: TimeZone[],
) => {
  if (saved && timeZoneOptions.some((tz) => tz.tzCode === saved)) return saved;

  return findLocalTimezone(timeZoneOptions)?.tzCode || "";
};

const getReminderTimezoneOptions = () => {
  const timeZoneOptions = buildTimeZoneList().map((tz) => ({
    ...tz,
    label: tz.label.split("/").pop() || "",
  }));
  const grouped = timeZoneOptions.reduce<
    { heading: string; items: { value: string; label: string }[] }[]
  >((groups, option) => {
    const existing = groups.find((group) => group.heading === option.region);
    const item = { value: option.tzCode, label: option.label };
    if (existing) {
      existing.items.push(item);
    } else {
      groups.push({ heading: option.region, items: [item] });
    }
    return groups;
  }, []);

  return { grouped, timeZoneOptions };
};

const getScheduleSnapshot = (
  value: ReminderScheduleValue | null | undefined,
  timeZoneOptions: TimeZone[],
) => ({
  enabled: value?.enabled ?? false,
  firstReminderDays: value?.firstReminderDays ?? 0,
  timezone: resolveTimezoneCode(value?.timezone, timeZoneOptions),
  effectiveFrom: value?.effectiveFrom ?? "",
  sendFollowUps: value?.sendFollowUps ?? false,
  repeatEveryDays: value?.repeatEveryDays ?? 0,
  maxFollowUps: value?.maxFollowUps ?? 0,
  stopOnManualSend: value?.stopOnManualSend ?? false,
});

const MAX_REMINDER_VALUE = 100;
// 0 days skips the weekday rule
const MIN_REMINDER_DAYS = 1;
// excludes the initial reminder so 0 is valid
const MIN_FOLLOW_UPS = 0;

const isInRange = (value: number, min: number) =>
  value >= min && value <= MAX_REMINDER_VALUE;

const rangeMessage = (min: number) =>
  `Enter a number between ${min} and ${MAX_REMINDER_VALUE}.`;

type ReminderScheduleSnapshot = ReturnType<typeof getScheduleSnapshot>;

type ReminderScheduleAction = "turnedOn" | "updated" | "turnedOff";

const wasPreviouslyConfigured = (
  value: ReminderScheduleValue | null | undefined,
) => Boolean(value?.enabled || value?.firstReminderDays || value?.timezone);

const reminderScheduleAnalyticsEvent = ({
  action,
  isFirstTurnOn,
  schedule,
  firmName,
}: {
  action: ReminderScheduleAction;
  isFirstTurnOn: boolean;
  schedule: ReminderScheduleSnapshot;
  firmName: string;
}) => ({
  category: "track" as const,
  name: "IntakeAutomaticRemindersConfigured",
  screen: "FirmSettingsMessageTemplates",
  entryPoint: "IntakeSettings",
  component: "Reminder Message",
  jiraKey: "LINK-3801",
  interactionType: "Active" as const,
  properties: {
    action,
    isFirstTurnOn,
    firstReminderDays: schedule.firstReminderDays,
    followUpsEnabled: schedule.sendFollowUps,
    repeatEveryDays: schedule.sendFollowUps ? schedule.repeatEveryDays : "",
    maxFollowUps: schedule.sendFollowUps ? schedule.maxFollowUps : "",
    cancelOnManualSend: schedule.stopOnManualSend,
    timeZone: schedule.timezone,
    cutoffDate: schedule.effectiveFrom || "All",
    firmName,
  },
});

export const ReminderScheduleSettings = ({
  value,
  update,
  loading,
  loadScheduledReminderCount,
}: ReminderScheduleSettingsProps) => {
  const { publishEvent } = useAnalyticsEvent();
  const { user } = useUser();
  const { grouped: timezoneOptions, timeZoneOptions } = useMemo(
    getReminderTimezoneOptions,
    [],
  );
  const hasTurnedOnBeforeRef = useRef(wasPreviouslyConfigured(value));
  const [enabled, setEnabled] = useState(value?.enabled ?? false);
  const [firstReminderDays, setFirstReminderDays] = useState(
    value?.firstReminderDays ?? 0,
  );
  const [timezone, setTimezone] = useState(() =>
    resolveTimezoneCode(value?.timezone, timeZoneOptions),
  );
  const [effectiveFrom, setEffectiveFrom] = useState(
    value?.effectiveFrom ?? "",
  );
  const [sendFollowUps, setSendFollowUps] = useState(
    value?.sendFollowUps ?? false,
  );
  const [repeatEveryDays, setRepeatEveryDays] = useState(
    value?.repeatEveryDays ?? 0,
  );
  const [maxFollowUps, setMaxFollowUps] = useState(value?.maxFollowUps ?? 0);
  const [stopOnManualSend, setStopOnManualSend] = useState(
    value?.stopOnManualSend ?? false,
  );
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDisable, setPendingDisable] = useState(false);
  const [scheduledFormCount, setScheduledFormCount] = useState(0);
  const [savedSchedule, setSavedSchedule] = useState(() =>
    getScheduleSnapshot(value, timeZoneOptions),
  );

  const currentSchedule = {
    enabled,
    firstReminderDays,
    timezone,
    effectiveFrom,
    sendFollowUps,
    repeatEveryDays,
    maxFollowUps,
    stopOnManualSend,
  };
  const hasUnsavedChanges =
    JSON.stringify(currentSchedule) !== JSON.stringify(savedSchedule);

  useEffect(() => {
    if (wasPreviouslyConfigured(value)) {
      hasTurnedOnBeforeRef.current = true;
    }
    const nextSchedule = getScheduleSnapshot(value, timeZoneOptions);
    setEnabled(nextSchedule.enabled);
    setFirstReminderDays(nextSchedule.firstReminderDays);
    setTimezone(nextSchedule.timezone);
    setEffectiveFrom(nextSchedule.effectiveFrom);
    setSendFollowUps(nextSchedule.sendFollowUps);
    setRepeatEveryDays(nextSchedule.repeatEveryDays);
    setMaxFollowUps(nextSchedule.maxFollowUps);
    setStopOnManualSend(nextSchedule.stopOnManualSend);
    setSavedSchedule(nextSchedule);
  }, [timeZoneOptions, value]);

  const firstReminderDaysInvalid = !isInRange(
    firstReminderDays,
    MIN_REMINDER_DAYS,
  );
  const repeatEveryDaysInvalid =
    sendFollowUps && !isInRange(repeatEveryDays, MIN_REMINDER_DAYS);
  const maxFollowUpsInvalid =
    sendFollowUps && !isInRange(maxFollowUps, MIN_FOLLOW_UPS);
  const firstReminderDaysError = showErrors && firstReminderDaysInvalid;
  const repeatEveryDaysError = showErrors && repeatEveryDaysInvalid;
  const maxFollowUpsError = showErrors && maxFollowUpsInvalid;
  const timezoneError = showErrors && !timezone;
  const isTurningOn = !savedSchedule.enabled && !pendingDisable;

  const loadCountAndConfirm = async () => {
    setSaving(true);
    try {
      const count = loadScheduledReminderCount
        ? await loadScheduledReminderCount()
        : 0;
      setScheduledFormCount(count);
      setConfirmOpen(true);
    } catch (error) {
      setPendingDisable(false);
      setEnabled(true);
      console.error("Failed to load scheduled reminder count:", error);
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    if (!enabled) {
      setEnabled(true);
      return;
    }

    setEnabled(false);
    setPendingDisable(true);
    await loadCountAndConfirm();
  };

  const cancelConfirm = () => {
    if (pendingDisable) setEnabled(true);
    setPendingDisable(false);
    setConfirmOpen(false);
  };

  const persistReminderSchedule = async () => {
    setSaving(true);

    let action: ReminderScheduleAction = "updated";
    if (pendingDisable) action = "turnedOff";
    else if (isTurningOn) action = "turnedOn";

    const scheduleForEvent =
      action === "turnedOff" ? savedSchedule : currentSchedule;
    const isFirstTurnOn =
      action === "turnedOn" && !hasTurnedOnBeforeRef.current;

    try {
      if (pendingDisable) {
        await update({ enabled: false });
        setSavedSchedule((current) => ({ ...current, enabled: false }));
      } else {
        await update({
          enabled: true,
          effectiveFrom,
          firstReminderDays,
          timezone,
          stopOnManualSend,
          sendFollowUps,
          repeatEveryDays: sendFollowUps ? repeatEveryDays : 0,
          maxFollowUps: sendFollowUps ? maxFollowUps : 0,
        });
        setSavedSchedule({ ...currentSchedule, enabled: true });
      }

      if (action === "turnedOn") hasTurnedOnBeforeRef.current = true;
      publishEvent?.(
        reminderScheduleAnalyticsEvent({
          action,
          isFirstTurnOn,
          schedule: scheduleForEvent,
          firmName: user?.teamName || "",
        }),
      ).catch(console.error);

      setShowErrors(false);
      setConfirmOpen(false);
      setPendingDisable(false);
    } catch (error) {
      if (pendingDisable) setEnabled(true);
      console.error("Failed to save reminder schedule:", error);
    } finally {
      setSaving(false);
    }
  };

  const saveReminderSchedule = async () => {
    if (
      firstReminderDaysInvalid ||
      repeatEveryDaysInvalid ||
      maxFollowUpsInvalid ||
      !timezone
    ) {
      setShowErrors(true);
      return;
    }

    if (isTurningOn) {
      setConfirmOpen(true);
      return;
    }

    await loadCountAndConfirm();
  };

  if (loading) {
    return <Spinner size="regular" color="primary" />;
  }

  return (
    <div className="flex flex-col gap-large">
      <Switch
        className="w-max"
        text="Schedule reminders via client portal"
        toggled={enabled}
        onClick={toggleEnabled}
        disabled={saving}
        dataTestId="reminder-schedule-enabled"
      />
      {enabled && (
        <>
          <div className="grid grid-cols-1 gap-large tablet:grid-cols-2 medium:grid-cols-3">
            <NumberStepper
              id="first-reminder-days"
              label="Initial reminder"
              mandatory
              tooltip="Sent a set number of days after the form is shared, on weekdays only."
              value={firstReminderDays}
              onChange={(nextValue) => setFirstReminderDays(nextValue ?? 0)}
              min={0}
              suffix="Days"
              disabled={saving}
              error={firstReminderDaysError}
              message={
                firstReminderDaysError
                  ? rangeMessage(MIN_REMINDER_DAYS)
                  : undefined
              }
              dataTestId="first-reminder-days"
            />
            <Select
              id="reminder-timezone"
              label="Time zone"
              mandatory
              placeholder="Select a time zone"
              value={timezone}
              options={timezoneOptions}
              onChange={(nextValue) => setTimezone(nextValue as string)}
              error={timezoneError}
              message={timezoneError ? "Select a time zone." : undefined}
              disabled={saving}
              dataTestId="reminder-timezone"
            />
            <SharedFormsSelect
              value={effectiveFrom}
              onChange={setEffectiveFrom}
              disabled={saving}
            />
          </div>
          <div className="grid grid-cols-1 gap-large tablet:grid-cols-2 medium:grid-cols-3">
            <div>
              <div className="mb-2 text-ui font-semibold">Reminders recur</div>
              <SegmentedControl
                className="w-max"
                index={sendFollowUps ? 1 : 0}
                items={[
                  {
                    label: "One-time",
                    onClick: () => setSendFollowUps(false),
                  },
                  {
                    label: "Recurring",
                    onClick: () => setSendFollowUps(true),
                  },
                ]}
              />
            </div>
            <NumberStepper
              id="repeat-every-days"
              label="Repeat every"
              mandatory
              value={repeatEveryDays}
              onChange={(nextValue) => setRepeatEveryDays(nextValue ?? 0)}
              min={0}
              suffix="Days"
              disabled={saving || !sendFollowUps}
              error={repeatEveryDaysError}
              message={
                repeatEveryDaysError
                  ? rangeMessage(MIN_REMINDER_DAYS)
                  : undefined
              }
              dataTestId="repeat-every-days"
            />
            <NumberStepper
              id="maximum-reminders"
              label="Maximum reminders"
              mandatory
              value={maxFollowUps}
              onChange={(nextValue) => setMaxFollowUps(nextValue ?? 0)}
              min={0}
              disabled={saving || !sendFollowUps}
              error={maxFollowUpsError}
              message={
                maxFollowUpsError ? rangeMessage(MIN_FOLLOW_UPS) : undefined
              }
              dataTestId="maximum-reminders"
            />
          </div>
          <Checkbox
            label="Cancel schedule if anyone sends a manual reminder on the matter."
            checked={stopOnManualSend}
            onChange={setStopOnManualSend}
            disabled={saving}
            dataTestId="reminder-stop-on-manual-send"
          />
          <div>
            <Button
              text="Save"
              variant="primaryDefault"
              size="small"
              onClick={saveReminderSchedule}
              loading={saving}
              disabled={!hasUnsavedChanges}
              dataTestId="save-reminder-schedule"
            />
          </div>
        </>
      )}
      <ReminderScheduleConfirmModal
        open={confirmOpen}
        isTurningOn={isTurningOn}
        isTurningOff={pendingDisable}
        scheduledFormCount={scheduledFormCount}
        loading={saving}
        onCancel={cancelConfirm}
        onConfirm={persistReminderSchedule}
      />
    </div>
  );
};
